import { type ComponentType, type FC, type JSX, type PropsWithChildren } from 'react';

export type { EventHint } from '@sentry/types';

export type { EventListenerCallback, UseEventDispatchHook } from '../events/core';
export type { CaptureExceptionPayload } from '../events/sentry-browser/bridge';

export type {
	Attributes,
	OperationalEventPayload,
	ScreenEventPayload,
	TrackEventPayload,
	UIEventPayload,
} from '../events/analytics-web-client-types';

// eslint-disable-next-line import/order
import {
	Breadcrumb,
	type BreadcrumbAttributes,
	breadcrumbMountedEventChannel,
	type CaptureExceptionPayload,
	internalGasV3OperationalEventChannel,
	internalGasV3ScreenEventChannel,
	internalGasV3TrackEventChannel,
	internalGasV3UIEventChannel,
	internalSentryExceptionChannel,
	type ScreenEventPayload,
} from '../channels';

export { Breadcrumb };
export type { BreadcrumbAttributes };

import type { Attributes, BaseToUIAnalyticsProps, Listener, UseEventDispatchHook } from '../events';
import { createAtlaskitToEventChannelRepackager } from '../events/atlaskit';
import {
	withBreadcrumb as baseWithBreadcrumb,
	type BreadcrumbMountedPayload,
} from '../events/breadcrumbs';

export const useGasV3UIEventDispatch: UseEventDispatchHook<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>
> = internalGasV3UIEventChannel.useBreadcrumbedEventDispatch;
export const useGasV3ScreenEventDispatch: UseEventDispatchHook<ScreenEventPayload> =
	internalGasV3ScreenEventChannel.useEventDispatch;
export const useGasV3TrackEventDispatch: UseEventDispatchHook<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>
> = internalGasV3TrackEventChannel.useBreadcrumbedEventDispatch;
export const useGasV3OperationalEventDispatch: UseEventDispatchHook<
	Omit<
		{
			action: string;
			actionSubject: string;
			actionSubjectId?: string;
			attributes?: Attributes;
			containerId?: string;
			containerType?: string;
			objectId?: string;
			objectType?: string;
			source: string;
			tags?: string[];
		},
		'source'
	>
> = internalGasV3OperationalEventChannel.useBreadcrumbedEventDispatch;

export const useSentryExceptionDispatch: UseEventDispatchHook<CaptureExceptionPayload> =
	internalSentryExceptionChannel.useBreadcrumbedEventDispatch;

export const RepackageAtlaskitEvent: FC<PropsWithChildren<BaseToUIAnalyticsProps>> =
	createAtlaskitToEventChannelRepackager(useGasV3UIEventDispatch);

/**
 * TODO: Old APIs need this to be exposed. Remove whatever's using this and then remove this.
 */
export const BreadcrumbMountedListener: Listener<BreadcrumbMountedPayload<BreadcrumbAttributes>> =
	breadcrumbMountedEventChannel.Listener;

export const withBreadcrumb = <ComponentPropsType extends {}>(
	Component: ComponentType<ComponentPropsType>,
	name: string,
	attributes?: BreadcrumbAttributes,
): ((props: ComponentPropsType) => JSX.Element) =>
	baseWithBreadcrumb(Component, Breadcrumb, name, attributes);
